import { useEditorialMediaUrl } from "@/lib/useEditorialMediaUrl";
import { editorialImageStyle, hasExplicitEditorialCrop } from "@/lib/editorialReaderLayout.mjs";
import { EditorialStoryFormattedParagraphV1 } from "../editorial-v2/EditorialStoryFormattedParagraphV1";

const BG = {
  "warm-ivory": "#EFE6D5",
  "mineral-plaster": "#E6DFCF",
  "almond-paper": "#F1E7D2",
  "deep-cocoa": "#2A1810",
  "full-image": "#EFE6D5",
};

const PALETTE = {
  ivory: "#EFE6D5",
  almond: "#D6B98A",
  olive: "#7E8A3E",
  cocoa: "#5B3B25",
  burgundy: "#7A2A2A",
  gold: "#C9A84C",
  pistachio: "#A6C088",
  apricot: "#E8B48A",
};

const serif = "'Cormorant Garamond', 'Playfair Display', serif";

function bgColor(key) {
  return BG[key] || BG["warm-ivory"];
}

function accentColor(key) {
  return PALETTE[key] || PALETTE.olive;
}

function StoryMedia({ src, media, alt = "", mobile = false }) {
  if (!src) return null;
  const cropped = hasExplicitEditorialCrop(media || {});
  const style = editorialImageStyle(media || {});

  return (
    <div className="flex w-full items-start justify-center overflow-hidden">
      <img
        src={src}
        alt={alt}
        className={cropped ? "h-full w-full object-cover" : "block h-auto w-auto max-w-full object-contain"}
        style={{
          ...style,
          maxHeight: cropped ? undefined : mobile ? "440px" : "700px",
        }}
      />
    </div>
  );
}

export function EditorialStoryReaderContract({ block, mode = "desktop", continuous = false }) {
  const c = block?.content || {};
  const s = block?.settings || {};
  const variant = s.variant || "A";
  const bg = bgColor(s.background);
  const dark = s.background === "deep-cocoa";
  const text = dark ? "#F1E4CB" : "#3B241A";
  const accent = accentColor(s.palette);
  const paragraphs = Array.isArray(c.extra?.paragraphs) ? c.extra.paragraphs.filter(Boolean) : [];

  const desktopSrc = useEditorialMediaUrl(c.hero_url, c.hero_media_id);
  const mobileOwn = useEditorialMediaUrl(c.hero_mobile_url, c.hero_mobile_media_id);
  const mobileSrc = mobileOwn || desktopSrc;

  const desktopMedia = {
    focal_x: s.focal_x ?? 0.5,
    focal_y: s.focal_y ?? 0.5,
    zoom: s.hero_zoom || 1,
  };
  const mobileMedia = {
    focal_x: s.mobile_focal_x ?? s.focal_x ?? 0.5,
    focal_y: s.mobile_focal_y ?? s.focal_y ?? 0.5,
    zoom: s.hero_mobile_zoom || 1,
  };

  const Copy = ({ mobile = false }) => (
    <div className="min-w-0" style={{ color: text }}>
      {c.eyebrow && (
        <div className="mb-4 text-[9px] uppercase tracking-[0.36em]" style={{ color: accent }}>
          {c.eyebrow}
        </div>
      )}
      <h1
        className="font-serif font-normal [overflow-wrap:anywhere]"
        style={{
          fontFamily: serif,
          fontSize: mobile ? "clamp(30px, 8vw, 38px)" : "clamp(40px, 4.2vw, 56px)",
          lineHeight: 1.03,
          maxWidth: continuous && !mobile ? "none" : mobile ? "12ch" : "13ch",
        }}>
        {c.headline || ""}
      </h1>
      {c.subtitle && (
        <div className={`mt-4 italic leading-[1.5] opacity-82 ${continuous ? "" : "max-w-[36ch]"} ${mobile ? "text-[15px]" : "text-[17px]"}`}>
          {c.subtitle}
        </div>
      )}
      <div className="mt-5 flex items-center gap-2">
        <span className="block h-px w-10" style={{ background: accent }} />
        <span className="block h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
      </div>
      {c.lead && (
        <p className={`mt-6 font-serif italic leading-[1.5] ${continuous ? "" : "max-w-[42ch]"} ${mobile ? "text-[16px]" : "text-[19px]"}`}>
          {c.lead}
        </p>
      )}
      {paragraphs.length > 0 && (
        <div className={continuous
          ? "mt-6 space-y-5 text-[16px] leading-[1.9] sm:text-[17px] [overflow-wrap:anywhere]"
          : `mt-6 max-w-[54ch] space-y-4 leading-[1.78] ${mobile ? "text-[14px]" : "text-[14px]"}` }
          data-testid={continuous ? "editorial-story-flow-prose" : undefined}>
          {paragraphs.map((p, i) => <EditorialStoryFormattedParagraphV1 key={i} text={p} />)}
        </div>
      )}
      {c.quote && (
        <blockquote
          className={`mt-7 border-l pl-5 font-serif italic leading-[1.45] ${continuous ? "" : "max-w-[44ch]"} ${mobile ? "text-[17px]" : "text-[19px]"}`}
          style={{ borderColor: accent }}>
          {c.quote}
        </blockquote>
      )}
      {(!continuous || mobile) && c.caption && <div className="mt-5 text-[10px] italic opacity-55">{c.caption}</div>}
    </div>
  );

  if (mode === "mobile") {
    return (
      <section className="w-full overflow-hidden px-6 py-7" style={{ background: bg }} data-testid="editorial-story-contract-mobile">
        {mobileSrc && (
          <div className="mb-6" data-testid={continuous ? "editorial-story-flow-mobile-media" : undefined}>
            <StoryMedia src={mobileSrc} media={mobileMedia} alt={c.alt} mobile />
          </div>
        )}
        <Copy mobile />
      </section>
    );
  }

  // Ordinary editorial stories flow naturally beside the image, then below
  // its measured height. The immutable Tome opening keeps the approved
  // separate longform Reader (continuous remains false there).
  if (continuous) {
    const photo = desktopSrc && (
      <figure
        className={
          "relative mb-6 w-[40%] max-w-[380px] overflow-hidden " +
          (hasExplicitEditorialCrop(desktopMedia) ? "aspect-[4/5] " : "") +
          (variant === "B" ? "float-right ml-8 lg:ml-10" : "float-left mr-8 lg:mr-10")
        }
        data-testid="editorial-story-flow-float"
      >
        <div className="h-full w-full flex items-start justify-center">
          <StoryMedia src={desktopSrc} media={desktopMedia} alt={c.alt} />
        </div>
        {c.caption && (
          <figcaption className="absolute bottom-2 left-2 bg-white/80 px-2 py-1 text-[10px] italic text-[#3B241A]">
            {c.caption}
          </figcaption>
        )}
      </figure>
    );
    return (
      <section
        className="mx-auto w-full max-w-[920px] px-6 py-10 lg:px-10 lg:py-14"
        style={{ background: bg }}
        data-testid="editorial-story-flow-desktop"
        data-variant={variant}
      >
        <div className="flow-root" data-testid="editorial-story-flow-content">
          {photo}
          <Copy />
        </div>
      </section>
    );
  }

  const image = desktopSrc ? (
    <div className="flex min-h-0 items-start justify-center pt-12 pb-12 lg:pt-14 lg:pb-14">
      <StoryMedia src={desktopSrc} media={desktopMedia} alt={c.alt} />
    </div>
  ) : null;

  const copy = (
    <div className="flex items-start px-8 py-12 lg:px-10 lg:py-14">
      <div className="w-full max-w-[500px]">
        <Copy />
      </div>
    </div>
  );

  return (
    <section className="mx-auto w-full max-w-[1120px] overflow-hidden" style={{ background: bg }} data-testid="editorial-story-contract-desktop">
      <div className={`grid items-start ${desktopSrc ? "lg:grid-cols-[48%_52%]" : ""}`}>
        {desktopSrc ? (variant === "B" ? <>{copy}{image}</> : <>{image}{copy}</>) : <div className="mx-auto w-full max-w-[760px]">{copy}</div>}
      </div>
    </section>
  );
}
